import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { ApiService } from '../../../../core/api-service';
import { FeedbackService } from '../../../../core/feedback.service';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { CC_OPERATIONS } from '../../../../models/locations-models/cc-row-setting';
import { ApiResponse, ApiResponseStatus } from '../../../../models/api-response';
import { CCMatrixResponsePayload } from '../../../../models/locations-models/cc-matrix-payload';
import * as locationListSelectors from '../../location-list/state/location-list-selectors';
import { selectAccountID } from '../../../../store/global/core-selectors';
import { sendToGoogleAnalytics } from '../../../../core/utilities';
import { selectCcLocationsArray } from './cc-list-selectors';
import { EditGbpConnectionsActions, LoadingSpinnerActions, LocationsLifecycleActions,
  LocationsRefreshActions, RowActions } from '../../location-list/state/location-list-actions';
import { CCActionBarActions, CCListRefreshActions, CCOperationActions, markAllCCListsAsStale, noop, resetCCData } from './cc-list-actions';
import { selectCCMatrixRequestPayload } from './cc-list-combined-selectors';
import { selectIsCcOn } from '../../state/location-skeleton-combined-selectors';
import { ClaimUnclaimResponse } from '../../../../models/claim-unclaim-response';

@Injectable()
export class CCListEffects {
  constructor(
      private actions$: Actions,
      private store: Store,
      private apiService: ApiService,
      private feedbackService: FeedbackService) {
  }

  // https://medium.com/simars/ngrx-http-effects-should-listen-for-cancel-action-227716178f70

  updateCCList = createEffect(() => {
    return this.actions$.pipe(
        ofType(
            CCListRefreshActions.updateCCList,
            RowActions.updateFocusedRow,
            // cancel when locations tab becomes inactive
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom((action) => [this.store.select(selectCCMatrixRequestPayload(
            action.type === RowActions.updateFocusedRow.type ? action.id : null)),
          this.store.select(selectIsCcOn),
          this.store.select(selectCcLocationsArray)],
        ),
        filter(([_, payload, ccOn, ccLocations]) => !!payload.focusedLocationId && ccOn),
        switchMap(([action, payload, _, ccLocations]) =>
            action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ?
                EMPTY :
                action.type === RowActions.updateFocusedRow.type && ccLocations?.length ? // do not fetch new CC candidates if there are some cached
                    EMPTY :
                    of(
                        CCListRefreshActions.requestNewCCMatrix({ payload }),
                        CCListRefreshActions.updateCCLocationsLoading({
                          focusedLocationId: payload.focusedLocationId,
                          loading: true,
                        }),
                    ),
        ),
    );
  });

  requestNewCCMatrix = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCListRefreshActions.requestNewCCMatrix),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            this.apiService.getCCMatrix(accountId.toString(), action.payload)
                .pipe(
                    mergeMap((res: ApiResponse<CCMatrixResponsePayload>) => [
                      CCListRefreshActions.updateCCLocationsLoading({ // stop loading spinner right away
                        focusedLocationId: action.payload.focusedLocationId,
                        loading: false,
                      }),
                      CCListRefreshActions.processCCMatrix({
                        payload: res.result, focusedLocationId: action.payload.focusedLocationId,
                      }),
                    ]),
                    catchError((err) => {
                      return of(CCListRefreshActions.updateCCListError({ focusedLocationId: action.payload.focusedLocationId }),
                          CCListRefreshActions.showUpdateCCListErrorSnackbar({
                            payload: action.payload,
                            error: err,
                          }));
                    }),
                ),
        ),
    );
  });

  processCCMatrix = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCListRefreshActions.processCCMatrix),
        switchMap((action) => {
          if (!action.payload?.candidates?.length) {
            // warning buttons
            const userChoice = new Subject<boolean>(); // true = 'create GBB location'

            const snackbarInstance = this.feedbackService.showWarningSnackbar({
              duration: 6000,
              data: {
                userFriendlyMessage: 'No search results found. Try adapting the search parameters or create a new location.',
                actionButtons: [{
                  text: 'New GBP location', action: () => userChoice.next(true),
                }],
                dismissButton: { action: () => userChoice.next(false) },
              },
            });

            // Register a 1-time handler to auto-dismiss the snackbar after the focused location changes
            // Prevents buggy behavior when user changes focused location and then selects 'create GMB location'
            this.store.select(locationListSelectors.selectFocusedLocation).pipe(
                filter((fl) => fl?.id !== action.focusedLocationId),
                take(1),
            ).subscribe(() => snackbarInstance?.dismiss());

            return userChoice.pipe(
                concatLatestFrom(() => this.store.select(locationListSelectors.selectFocusedLocation)),
                mergeMap(([userRes, focusedLocation]) => {
                      if (!userRes) { // dismiss
                        return [CCListRefreshActions.updateCCListSuccess({
                          // !important to use action.focussedLocationId as user might have switched focused location
                          focusedLocationId: action.focusedLocationId,
                          ccLocations: [],
                        })];
                      }
                      // create GMB loc
                      else if (userRes && focusedLocation && !focusedLocation.foundInMyBusiness) {
                        sendToGoogleAnalytics('Single-location action',
                            'create GMB location',
                            'Create a new GMB location for a BLS location');

                        return [
                          EditGbpConnectionsActions.editGbpConnections({
                            location: focusedLocation,
                            command: 'create',
                          }),
                          CCListRefreshActions.updateCCListSuccess({
                            focusedLocationId: focusedLocation.id,
                            ccLocations: [],
                          }),
                        ];
                      } else { // could not create GMB location
                        console.warn(
                            `Could not create new GBP location \n 
                                          Focused location: ${!!focusedLocation} \n 
                                          Focused location found in My Business: ${focusedLocation.foundInMyBusiness}`,
                        );
                        return [CCListRefreshActions.updateCCListSuccess({
                          focusedLocationId: focusedLocation.id,
                          ccLocations: [],
                        })];
                      }
                    },
                ));
          } else {
            // store cc list
            return [CCListRefreshActions.updateCCListSuccess({
              focusedLocationId: action.focusedLocationId,
              ccLocations: action.payload.candidates,
              lscLocationIdsToBeSelected: action.payload.lscLocationIdsToBeSelected
            })];
          }
        }),
    );
  });

  showCCListErrorSnackbar$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCListRefreshActions.showUpdateCCListErrorSnackbar),
        switchMap((action) => {
          const userChoice = new Subject<boolean>(); // true = 'Refresh'

          // show snackbar
          const snackbarInstance = this.feedbackService.showAlertSnackbarWithRetryFromEffect({
            userFriendlyMessage: 'Fetching Claim & Clean candidates failed.',
            duration: 6000,
            err: action.error,
            userChoice
          });

          // Register a 1-time handler to auto-dismiss the snackbar after the focused location changes
          // Prevents buggy behavior when user changes focused location and then selects 'create GMB location'
          this.store.select(locationListSelectors.selectFocusedLocation).pipe(
              filter((fl) => fl?.id !== action.payload.focusedLocationId),
              take(1),
          ).subscribe(() => snackbarInstance?.dismiss());

          return userChoice.pipe(mergeMap((res) => res ?
              [CCListRefreshActions.updateCCLocationsLoading({
                focusedLocationId: action.payload.focusedLocationId,
                loading: true,
              }),
                CCListRefreshActions.requestNewCCMatrix({ payload: action.payload })] :
              [noop()],
          ));
        }),
    );
  });


  /**
   * Sets the currently selected radius
   */
  setDefaultCCRadius = createEffect(() => {
    return this.actions$.pipe(
        ofType(LocationsLifecycleActions.locationsComponentInitiated, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountID]) =>
                action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type ?
                    this.apiService.getClaimCleanRadius(accountID)
                        .pipe(
                            map((radius: number) => {
                              return CCActionBarActions.updateCCRadiusSuccess({ ccSearchRadius: radius });
                            }),
                            catchError((err: HttpErrorResponse) => {
                              this.feedbackService.showAlertSnackbar({
                                data: {
                                  userFriendlyMessage: 'Updating Claim & Clean search radius failed.',
                                  detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                                },
                              }, err?.status);
                              return of(LoadingSpinnerActions.stopLoadingSpinner());
                            })) :
                    EMPTY,
        ),
    );
  });


  /**
   * Fetches claim-and-clean keywords saved on account-level
   */
  setDefaultCCKeywords = createEffect(() => {
    return this.actions$.pipe(
        ofType(LocationsLifecycleActions.locationsComponentInitiated, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountID]) =>
                action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type ?
                    this.apiService.getClaimCleanKeywords(accountID).pipe(
                        map((keywords) => CCActionBarActions.updateCCKeywordsSuccess({ ccKeywords: keywords })),
                        catchError((err: HttpErrorResponse) => {
                          this.feedbackService.showAlertSnackbar({
                            data: {
                              userFriendlyMessage: 'Getting Claim & Clean keywords failed.',
                              detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                            },
                          }, err?.status);
                          return of(LoadingSpinnerActions.stopLoadingSpinner());
                        })) :
                    EMPTY,
        ),
    );
  });

  /**
   * Reset stored candidates on accountID change
   */
  resetCCLists = createEffect(() => {
    return this.store.select(selectAccountID).pipe(
        filter((accountId) => !!accountId),
        map(() => resetCCData()),
    );
  });

  /**
   * Updates cc search radius on the BE and reload cc results
   */
  updateRadius = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCActionBarActions.updateCCRadius, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type ?
                this.apiService.setClaimCleanRadius(accountId, action.ccSearchRadius)
                    .pipe(
                        mergeMap(() => {
                          return [CCActionBarActions.updateCCRadiusSuccess({ ccSearchRadius: action.ccSearchRadius }),
                            markAllCCListsAsStale(), // mark all cc lists as stale for now
                            CCListRefreshActions.updateCCList(), // update just the list for the currently focused location (will unmark that list as well)
                          ];
                        }),
                        catchError((err: HttpErrorResponse) => {
                          this.feedbackService.showAlertSnackbar({
                            data: {
                              userFriendlyMessage: 'Updating Claim & Clean search radius failed.',
                              detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                            },
                          }, err?.status);
                          return of(LoadingSpinnerActions.stopLoadingSpinner());
                        })) : EMPTY,
        ),
    );
  });

  /**
   * Updates the claim-and-clean keywords saved on account-level and reloads c&c-results
   */
  updateCCKeywords = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCActionBarActions.updateCCKeywords, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type ?
                this.apiService.setClaimCleanKeywords(accountId, action.ccKeywords)
                    .pipe(
                        mergeMap(() => [
                          CCActionBarActions.updateCCKeywordsSuccess({ ccKeywords: action.ccKeywords }),
                          markAllCCListsAsStale(), // mark all cc lists as stale for now
                          CCListRefreshActions.updateCCList(), // update just the list for the currently focused location (will unmark that list as well)
                        ]),
                        catchError((err: HttpErrorResponse) => {
                          this.feedbackService.showAlertSnackbar({
                            data: {
                              userFriendlyMessage: 'Updating Claim & Clean keywords failed.',
                              detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                            },
                          }, err?.status);
                          return of(LoadingSpinnerActions.stopLoadingSpinner());
                        })) : EMPTY,
        ),
    );
  });

  /**
   * Starts CC operation
   */
  startCcOperation = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCOperationActions.ccOperation, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => [this.store.select(selectAccountID),
          this.store.select(locationListSelectors.selectFocusedLocation)]),
        switchMap(([action, accountId, focusedLocation]) =>
            action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type ?
                this.apiService.requestCcOperation(focusedLocation.id,
                    action.ccRowSetting.placeId,
                    accountId,
                    action.ccOperation,
                    action.starredState,
                ).pipe(mergeMap((res) => {
                  // stop spinner
                  const ccRowSettingCopyWithNoSpinner = { ...action.ccRowSetting };
                  ccRowSettingCopyWithNoSpinner.ongoingCcOperation = null;

                  // check for warnings - introduced in BLS-2737
                  if (res?.status === ApiResponseStatus.WARNING) {
                    this.feedbackService.showWarningSnackbar({
                      data: {
                        userFriendlyMessage: 'Claiming and transferring location was partially successful.',
                        detailedInfoFromBE: [res?.message],
                      },
                    });
                  }

                  const returnActions: any[] = [
                    // stop spinner
                    CCOperationActions.ccOperationSuccess({
                      focusedLocationId: focusedLocation.id,
                      ccRowSetting: ccRowSettingCopyWithNoSpinner,
                      ccOperation: action.ccOperation
                    }),
                    CCListRefreshActions.updateCCList(), // needs to be called every time, BLS-6044
                    markAllCCListsAsStale(), // mark all cc lists as stale for now
                  ];

                  if (action.ccOperation === CC_OPERATIONS.CLAIM ||
                      action.ccOperation === CC_OPERATIONS.UNDO_CLAIM) {
                    const result = res.result as ClaimUnclaimResponse;

                    if (result.parent) { // this response should be correct, but the child response
                      // seems to be off - BLS-6044
                      returnActions.push(LocationsRefreshActions.patchLocation({ location: result.parent }));
                    }

                  }

                  return returnActions;
                }),
                catchError((err: HttpErrorResponse) => {
                  const ccRowSettingCopy = { ...action.ccRowSetting };
                  // alert buttons
                  const userChoice = new Subject<boolean>(); // true means 'Retry'
                  let errorMessage: string;

                  // determine error message
                  switch (action.ccOperation) {
                    case CC_OPERATIONS.CLAIM:
                      errorMessage = 'Could not claim Google Maps location.';
                      break;
                    case CC_OPERATIONS.MARK_AS_DUPE:
                      errorMessage = 'Could not mark Google Maps location as a duplicate.';
                      break;
                    case CC_OPERATIONS.CHANGE_STARRING:
                      errorMessage = 'Could not change starring of Google Maps location.';
                      break;
                    default:
                      errorMessage = 'An unknown error occurred.';
                  }

                  this.feedbackService.showAlertSnackbarWithRetryFromEffect({
                    userFriendlyMessage: errorMessage,
                    err,
                    userChoice
                  });

                  return userChoice.pipe(switchMap(res => {
                    if (res) {
                      // restart operation
                      return of(CCOperationActions.ccOperation({
                        focusedLocationId: focusedLocation.id,
                        ccRowSetting: ccRowSettingCopy,
                        ccOperation: action.ccOperation,
                        starredState: action.starredState,
                      }));
                    } else {
                      // stop spinner
                      const ccRowSettingCopyWithNoSpinner = { ...action.ccRowSetting };
                      ccRowSettingCopyWithNoSpinner.ongoingCcOperation = null;
                      return of(CCOperationActions.ccOperationError({
                        focusedLocationId: focusedLocation.id,
                        ccRowSetting: ccRowSettingCopyWithNoSpinner,
                      }));
                    }
                  }));
                })) : EMPTY,
        ),
    );
  });

}
